import { useSubscription } from '@apollo/client';
import {
  Button,
  CreateButton,
  Dropdown,
  EditButton,
  ExportButton,
  Icons,
  List,
  Menu,
  Popconfirm,
  ShowButton,
  Space,
  Table,
  TableProps,
  Tag,
  Tooltip,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { CanAccess, IResourceComponentsProps, useCan, useNavigation } from '@pankod/refine-core';
import { useSearchParams } from '@pankod/refine-react-router-v6';
import { SearchWithDateRange } from 'components/search/SearchWithDateRange';
import CustomTableWithPagination from 'components/table/custom-table';
import { EStatus } from 'enums';
import { useUserData } from 'hooks';
import { IQueryOptionals, ISale } from 'interfaces';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { RiArticleLine } from 'react-icons/ri';
import { SearchIndex } from 'services/meilisearch';
import { LowStockFloatingWidget } from 'components/sales/LowStockFloatingWidget';
import {
  EInvoiceType,
  deleteSale,
  getCashMemoSale,
  getSaleInvoice,
  getSales,
  getSalesDropdown,
  updateSaleStatus,
} from 'services/sale';
import { MESSAGE_ADDED_SUBSCRIPTION } from 'services/subscription';
import { getWarehousesDropdown } from 'services/warehouse';
import { getActivityHistoryUrl } from 'utilities/activity-log';
import { downloadCSVFile } from 'utilities/file';
import {
  formatCurrency,
  getAddressAsString,
  getDateRangeOfLastXMonths,
  previewPdfFromBase64,
} from 'utilities/shared';
import { useFeatureFlag } from 'hooks/useFeatureFlags';
import { getAllCompanyConfig, getCompanyInfo } from 'services/config';
import {
  buildCashMemoReceiptData,
  chooseSaleReprintTemplate,
  renderCashMemo,
} from 'utilities/cash-memo';
import { getInvoiceHeader } from 'services/invoice-profile';
import { invoiceHeaderToReceiptCompany } from 'utilities/invoice-profile';
import { printPosInvoice } from 'utilities/print';
import styles from './style.module.css';
import MetaData from 'utilities/metadata';

const { Text } = Typography;

type IQuery = IQueryOptionals & { statuses?: EStatus[]; warehouse?: string };

export const SaleList: React.FC<IResourceComponentsProps> = () => {
  const [sales, setSales] = useState<ISale[]>([]);
  const [totalSales, setTotalSales] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>(() => {
    const [startDate, endDate] = getDateRangeOfLastXMonths(3);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  });
  const [statuses, setStatuses] = useState<EStatus[]>(() => {
    const localStatuses = localStorage.getItem('salesStatus');
    if (localStatuses) return localStatuses.split(',') as EStatus[];
    return [];
  });
  const [selectedWarehouse, setSelectedWarehouse] = useState<string[]>(() => {
    const localSalesWarehouse = localStorage.getItem('salesWarehouse');
    if (localSalesWarehouse) return [localSalesWarehouse];
    return [];
  });
  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setSearchParams] = useSearchParams();
  // Listen for new messages using GraphQL Subscription
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const { data: newSubscriptionData } = useSubscription(MESSAGE_ADDED_SUBSCRIPTION);
  const [actionsDisable, setActionsDisable] = useState<Record<string, boolean>>({});
  const { push } = useNavigation();
  const { data: canAccessDelete } = useCan({
    resource: 'sales',
    action: 'delete',
    params: { field: 'id' },
  });
  const userData = useUserData();

  const query = useMemo<IQuery>(() => {
    return {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      searchString: searchText,
      statuses,
      warehouse: selectedWarehouse[0] ?? undefined,
      start: page,
      limit,
    };
  }, [
    dateRange.startDate,
    dateRange.endDate,
    searchText,
    statuses,
    selectedWarehouse,
    page,
    limit,
  ]);

  const fetchSales = useCallback(async (query: IQuery) => {
    setIsLoading(true);
    try {
      const response = await getSales(query);
      setSales(response.data);
      setTotalSales(response.total);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchSales(query);
  }, [fetchSales, query]);

  useEffect(() => {
    if (newSubscriptionData) {
      setSubscriptionData(newSubscriptionData); // Update state when new data arrives
    }
  }, [newSubscriptionData]);

  // Update the sales list when new data is received
  useEffect(() => {
    if (subscriptionData && subscriptionData.messageAdded) {
      let parsedData: { instanceId: string; message: string } = {
        instanceId: '',
        message: '',
      };
      try {
        parsedData = JSON.parse(subscriptionData.messageAdded.content);
      } catch (error) {
        console.error('Error parsing message content:', error);
      }

      // Check if the instanceId matches the current platform ID
      if (!parsedData.instanceId || userData?.user?.platform?.id !== parsedData.instanceId) {
        return;
      }

      notification.success({
        message: 'Message',
        description: `${parsedData.message}`,
      });
      fetchSales(query); // Fetch latest sales
      // Reset subscriptionData to null after processing
      setSubscriptionData(null); // Prevent re-triggering
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionData, query]);

  useEffect(() => {
    (async () => {
      try {
        const warehouses = await getWarehousesDropdown();
        setWarehouses(warehouses.map((w) => ({ id: w.id, name: w.name })));
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteConfirmHandler = async (id: string) => {
    try {
      await deleteSale(id);
      await fetchSales({
        startDate: query.startDate,
        endDate: query.endDate,
      });
      notification.success({
        message: 'Success',
        description: 'Successfully deleted a sale',
      });
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to delete sale',
      });
    }
  };

  const onSearch = async (values: {
    startDate?: string;
    endDate?: string;
    searchString: string;
  }) => {
    setDateRange({ startDate: values.startDate, endDate: values.endDate });
    setSearchText(values.searchString);
    setPage(1);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const salesData = await getSalesDropdown(query.startDate, query.endDate);
      const rows = [['Order ID', 'Date', 'Status', 'Client Name', 'Phone', 'Amount']];
      salesData.forEach((element) => {
        const clientName = element.client.name;
        const clientPhoneNo = element.client.mobileNumber;
        rows.push([
          element.id,
          element.salesDate.toString(),
          element.salesStatus,
          clientName,
          clientPhoneNo,
          element.totalAmount.toString(),
        ]);
      });

      downloadCSVFile(rows);
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to Export CSV File.',
      });
    }
    setIsExporting(false);
  };

  const pageHeaderButtons = (
    <div className={styles['page-header__actions']}>
      <CreateButton type="primary" />
      <Button size="middle" icon={<Icons.ImportOutlined />} onClick={() => push('/import')}>
        Import
      </Button>
      <ExportButton loading={isExporting} onClick={handleExport} />

      <CanAccess resource="activity-history" action="list">
        <Button
          icon={<Icons.HistoryOutlined />}
          onClick={() => push(getActivityHistoryUrl('sales'))}
        >
          Activity
        </Button>
      </CanAccess>
    </div>
  );

  const onTableChange: TableProps<ISale>['onChange'] = async (
    pagination,
    filters,
    _sorter,
    _extra,
  ) => {
    const page = pagination.current ?? 1;
    const limit = pagination.pageSize ?? 10;
    setPage(page);
    setLimit(limit);
    setStatuses(() => {
      const statuses = (filters?.salesStatus ?? []) as EStatus[];
      localStorage.setItem('salesStatus', statuses.join(','));
      return statuses;
    });
    setSelectedWarehouse(() => {
      const warehouse = (filters['warehouse.name'] ?? []) as string[];
      localStorage.setItem('salesWarehouse', warehouse.join(','));
      return warehouse;
    });
  };

  const handleStatusUpdate = async (id: string, status: EStatus) => {
    setActionsDisable((prev) => ({ ...prev, [id]: true }));
    try {
      await updateSaleStatus(id, status);
      await fetchSales(query);
      notification.success({
        message: 'Success',
        description: 'Successfully changed status',
      });
    } catch (_) {
      notification.error({
        message: 'Failed',
        description: 'Failed to change status',
      });
    } finally {
      setActionsDisable(({ [id]: _, ...rest }) => rest);
    }
  };

  const onPageChange: any = async (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  const statusStyles: Record<EStatus | string, { textColor: string; backgroundColor: string }> = {
    [EStatus.CONFIRMED]: {
      textColor: '#1E88E5', // Blue for text
      backgroundColor: '#1E88E50D', // Light blue for background
    },
    [EStatus.IN_PROGRESS]: {
      textColor: '#FFA500', // Orange for text
      backgroundColor: '#FFA5001A', // Light orange for background
    },
    [EStatus.COMPLETED]: {
      textColor: '#43A047', // Green for text
      backgroundColor: '#43A0470D', // Light green for background
    },
    [EStatus.CANCELLED]: {
      textColor: '#E53935', // Red for text
      backgroundColor: '#E539350D', // Light red for background
    },
  };

  const getStatusStyles = (status: string) => {
    const normalizedStatus = status.trim().toLowerCase();
    const matchedStatus = Object.entries(EStatus).find(
      ([, value]) => value.toLowerCase() === normalizedStatus,
    )
      ? statusStyles[status as EStatus]
      : statusStyles[EStatus.CANCELLED]; // Default to cancel if not matched.

    return matchedStatus || { textColor: '#000000', backgroundColor: '#FFFFFF' }; // Fallback to default
  };

  return (
    <>
      <List headerProps={{ extra: pageHeaderButtons }}>
        <MetaData />
        <SearchWithDateRange
          placeholder="Search by Client, ID, Tags, Product, Warehouse"
          size="large"
          className={styles['filter-section__search']}
          index={SearchIndex.Sales}
          groups={['client', 'id', 'product', 'tag', 'warehouse']}
          onSearch={onSearch}
        />

        <CustomTableWithPagination
          total={totalSales}
          page={page}
          limit={limit}
          onPageChange={onPageChange}
        >
          <Table
            dataSource={sales}
            loading={isLoading}
            rowKey="id"
            scroll={{ x: true }}
            onChange={onTableChange}
            pagination={false}
          >
            <Table.Column<ISale> dataIndex="id" title="Order ID" />
            <Table.Column<ISale>
              dataIndex="salesDate"
              title="Date"
              render={(_, record): React.ReactNode => {
                const date = new Date(record.salesDate).toLocaleDateString('en-GB');
                return <Text>{date}</Text>;
              }}
            />
            <Table.Column
              dataIndex="salesStatus"
              title="Status"
              defaultFilteredValue={statuses}
              filters={[
                {
                  text: 'Order Confirmed',
                  value: EStatus.CONFIRMED,
                },
                {
                  text: 'Delivery In Progress',
                  value: EStatus.IN_PROGRESS,
                },
                {
                  text: 'Completed',
                  value: EStatus.COMPLETED,
                },
                {
                  text: 'Cancelled',
                  value: EStatus.CANCELLED,
                },
              ]}
              filterMultiple={true}
              render={(status: string) => {
                const { textColor, backgroundColor } = getStatusStyles(status);

                return (
                  <Tag
                    style={{
                      color: textColor,
                      backgroundColor: backgroundColor,
                      height: '24px',
                      padding: '0px 8px',
                      gap: '4px',
                      borderRadius: '8px',
                      opacity: 1,
                      border: 'none',
                      fontFamily: 'Nunito Sans',
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: '22px',
                    }}
                  >
                    {status}
                  </Tag>
                );
              }}
            />
            <Table.Column<ISale>
              dataIndex={['client', 'name']}
              title="Client Name"
              render={(value) => (
                <Tag
                  onClick={() => setSearchParams({ client: value })}
                  style={{ cursor: 'pointer' }}
                >
                  {value}
                </Tag>
              )}
            />
            <Table.Column<ISale>
              dataIndex={['warehouse', 'name']}
              title="Warehouse"
              render={(value, _record) => {
                return <Text>{value}</Text>;
              }}
              defaultFilteredValue={selectedWarehouse}
              filters={warehouses.map((w) => ({ text: w.name, value: w.id }))}
              filterSearch
              filterMultiple={false}
            />
            <Table.Column<ISale>
              dataIndex="totalAmount"
              title="Amount"
              render={(_, record) => formatCurrency(record.totalAmount)}
            />
            <Table.Column<ISale>
              title="Actions"
              dataIndex="actions"
              key="actions"
              width={120}
              render={(_, record) => {
                const displayActions = !(record.salesStatus === EStatus.COMPLETED);

                return (
                  <Space style={{ display: 'flex', alignItems: 'center' }}>
                    <ShowButton size="small" recordItemId={record.id} hideText />
                    {displayActions && (
                        <EditButton size="small" recordItemId={record.id} hideText />
                      ) &&
                      false}
                    <Popconfirm
                      title="Are you sure?"
                      okText="Delete"
                      onConfirm={() => deleteConfirmHandler(record.id)}
                      disabled={!canAccessDelete?.can}
                    >
                      <Button
                        size="small"
                        danger
                        icon={<Icons.DeleteOutlined />}
                        disabled={!canAccessDelete?.can}
                      />
                    </Popconfirm>
                    <InvoiceButton saleId={record.id} />
                    <Tooltip title={<span>User: {record.createdBy?.name}</span>}>
                      <Button size="small" icon={<Icons.InfoOutlined />} />
                    </Tooltip>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <StatusUpdateAction
                        record={record}
                        handleStatusUpdate={handleStatusUpdate}
                        disabled={actionsDisable[record.id]}
                      />
                    </div>
                  </Space>
                );
              }}
            />
          </Table>
        </CustomTableWithPagination>
      </List>
      <LowStockFloatingWidget />
    </>
  );
};

type InvoiceButtonProps = {
  saleId: string;
};

const InvoiceButton: React.FC<InvoiceButtonProps> = (props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const cashMemoPreviewEnabled = useFeatureFlag('pos.cashMemoPreview');
  const invoiceProfilesEnabled = useFeatureFlag('sale.invoiceProfiles');

  // With `pos.cashMemoPreview` on, a POS sale reprints as the cash memo (the POS receipt); every
  // other sale keeps the full invoice.
  const printCashMemoIfPosSale = async (id: string): Promise<boolean> => {
    if (!cashMemoPreviewEnabled) return false;
    const { salesChannel, cashMemoSale } = await getCashMemoSale(id);
    if (chooseSaleReprintTemplate(salesChannel, cashMemoPreviewEnabled) !== 'cashMemo') {
      return false;
    }
    let receiptCompany;
    if (invoiceProfilesEnabled) {
      // Aziz Auto A5: the header the sale was billed under, even after the default changed.
      receiptCompany = invoiceHeaderToReceiptCompany(await getInvoiceHeader({ saleId: id }));
    } else {
      const companyConfigs = await getAllCompanyConfig();
      const companyInfo = await getCompanyInfo(companyConfigs[0].id);
      receiptCompany = {
        name: companyInfo.companyName,
        number: companyInfo.companyNumber,
        address: getAddressAsString(companyInfo.address),
        tax: companyInfo.companyVatNo,
        bin: companyInfo.companyBinNo,
      };
    }
    printPosInvoice(renderCashMemo(buildCashMemoReceiptData(cashMemoSale, receiptCompany)));
    return true;
  };

  const handleInvoice = async (id: string) => {
    setIsLoading(true);
    try {
      if (await printCashMemoIfPosSale(id)) {
        setIsLoading(false);
        return;
      }
      const invoiceString = await getSaleInvoice(id, EInvoiceType.Sales);
      previewPdfFromBase64(invoiceString);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsLoading(false);
  };

  return (
    <Button
      size="small"
      loading={isLoading}
      icon={<RiArticleLine size={16} style={{ marginTop: '3px' }} />}
      onClick={() => handleInvoice(props.saleId)}
    />
  );
};

type StatusUpdateActionProps = {
  record: ISale;
  handleStatusUpdate: (id: string, status: EStatus) => Promise<void>;
  disabled?: boolean;
};

const getDisabledState = (status: EStatus) => {
  switch (status) {
    case EStatus.CONFIRMED:
      return {
        confirmedDisabled: true,
        inProgressDisabled: false,
        completedDisabled: false,
        cancelledDisabled: false,
      };
    case EStatus.IN_PROGRESS:
      return {
        confirmedDisabled: true,
        inProgressDisabled: true,
        completedDisabled: false,
        cancelledDisabled: false,
      };
    case EStatus.COMPLETED:
      return {
        confirmedDisabled: true,
        inProgressDisabled: true,
        completedDisabled: true,
        cancelledDisabled: false,
      };
    case EStatus.CANCELLED:
      return {
        confirmedDisabled: true,
        inProgressDisabled: true,
        completedDisabled: true,
        cancelledDisabled: true,
      };
    default:
      return {
        confirmedDisabled: false,
        inProgressDisabled: false,
        completedDisabled: false,
        cancelledDisabled: false,
      };
  }
};

const StatusUpdateAction: React.FC<StatusUpdateActionProps> = ({
  record,
  handleStatusUpdate,
  disabled,
}) => {
  const { confirmedDisabled, inProgressDisabled, completedDisabled, cancelledDisabled } =
    getDisabledState(record.salesStatus as EStatus);
  const moreMenu = (record: ISale) => (
    <Menu mode="vertical" onClick={({ domEvent }) => domEvent.stopPropagation()}>
      <Menu.Item
        key="confirmed"
        disabled={confirmedDisabled || disabled}
        onClick={() => handleStatusUpdate(record.id, EStatus.CONFIRMED)}
      >
        Order Confirmed
      </Menu.Item>
      <Menu.Item
        key="progress"
        disabled={inProgressDisabled || disabled}
        onClick={() => handleStatusUpdate(record.id, EStatus.IN_PROGRESS)}
      >
        Delivery In Progress
      </Menu.Item>
      <Menu.Item
        key="completed"
        disabled={completedDisabled || disabled}
        onClick={() => handleStatusUpdate(record.id, EStatus.COMPLETED)}
      >
        Completed
      </Menu.Item>
      <Popconfirm
        title="Are you sure?"
        okText="Yes"
        cancelText="No"
        onConfirm={() => handleStatusUpdate(record.id, EStatus.CANCELLED)}
        disabled={cancelledDisabled || disabled}
      >
        <Menu.Item key="cancelled" disabled={cancelledDisabled || disabled}>
          Cancelled
        </Menu.Item>
      </Popconfirm>
    </Menu>
  );

  return (
    <Dropdown overlay={moreMenu(record)} trigger={['click']}>
      <Icons.MoreOutlined
        onClick={(e) => e.stopPropagation()}
        style={{
          fontSize: 24,
        }}
      />
    </Dropdown>
  );
};
